import { DataGrid } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChartBindingValue, CustomChartTablePayload } from '@/entities/dashboards';
import { ChartCardShell } from '../ChartCardShell';
import type { ChartAppearance } from '../chartElements';
import { chartTableHeight, chartTableStyle } from '../chartLayout';
import { buildTableColumns, toIndexedRows } from './chartTable';
import { ChartLegend } from './ChartLegend';
import { valueRuleLegendItems } from './chartLegendItems';
import { useChartTableState } from './useChartTableState';

type Props = {
  /** Диаграмма, чьей таблице принадлежит личный вид: состав колонок, упорядочение и фильтры */
  chartId: string;
  title: string;
  payload: CustomChartTablePayload;
  /** Оформление диаграммы: правила раскраски числовых значений по величине */
  appearance?: ChartAppearance;
  /** Высота диаграммы задана пользователем — таблица прокручивает строки внутри неё */
  hasFixedHeight?: boolean;
  /** Выбор строки наполняет связанную диаграмму; не задан — строки не выбираются */
  onSelectElement?: (value: ChartBindingValue) => void;
};

/**
 * Таблица: колонки и строки приходят готовыми — клиент их не пересчитывает, но показывает в личном
 * виде смотрящего: состав и порядок колонок, упорядочение и фильтры настраиваются теми же
 * средствами кита, что в таблице «Все детали», и хранятся по каждой диаграмме отдельно.
 *
 * Выбранная строка опознаётся значением первой колонки: им же наполняется связанная диаграмма.
 */
export const TableChartView: FC<Props> = ({ chartId, title, payload, appearance, hasFixedHeight, onSelectElement }) => {
  const { t } = useTranslation('qualityDashboard');
  const columns = useMemo(
    () => buildTableColumns(payload.columns, payload.rows, appearance?.valueColorRules),
    [payload.columns, payload.rows, appearance],
  );
  const legendItems = useMemo(() => valueRuleLegendItems(appearance?.valueColorRules, t), [appearance, t]);
  const indexedRows = useMemo(() => toIndexedRows(payload.rows), [payload.rows]);
  const { gridProps, onResetColumns, rows, sorting, onSortingChange, filters, onFiltersChange } = useChartTableState({
    chartId,
    columns: payload.columns,
    rows: indexedRows,
  });

  const keyColumn = payload.columns[0]?.code;

  return (
    <ChartCardShell title={title} scrollable={false}>
      <DataGrid
        {...gridProps}
        columns={columns}
        dataSource={{ mode: 'client', data: rows }}
        getRowId={(row) => row.key}
        enableColumnResizing
        enableColumnReordering
        sortMode="single"
        sorting={sorting}
        onSortingChange={onSortingChange}
        filters={filters}
        onFiltersChange={onFiltersChange}
        columnsPanel={{ onReset: onResetColumns }}
        onRowClick={onSelectElement && keyColumn ? (row) => onSelectElement(row.values[keyColumn] ?? null) : undefined}
        height={chartTableHeight(rows.length, hasFixedHeight)}
        style={chartTableStyle(hasFixedHeight)}
      />
      <ChartLegend items={legendItems} />
    </ChartCardShell>
  );
};
